/**
 * Commercial-building warning for the cart-page delivery widget.
 *
 * The widget starts on "Commercial / Non-residential" (set in
 * snippets/delivery-scheduler.liquid) so the to-unit surcharge is shown up
 * front. A customer who then switches to "Residential" gets a confirmation
 * explaining the surcharge and the cancellation risk of picking the wrong venue
 * type.
 *
 * The delegated change listener survives section re-renders and only reacts to
 * user-initiated changes, so restoring a saved selection never opens the
 * warning. The surcharge amount is read from the same settings the fee
 * calculation uses.
 */
(function () {
  'use strict';

  var ROOT_ID = 'delivery-commercial-popup';
  var STYLE_ID = 'delivery-commercial-popup-style';

  var BODY = 'A $3 to-unit delivery surcharge applies to all commercial buildings, ' +
    'including residences within mixed developments above shopping malls. ' +
    'Please select the correct venue type to avoid order cancellation.';

  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function surcharge() {
    var o = readJSON('delivery-config-overrides') || {};
    var s = readJSON('delivery-settings-data') || {};
    var v = o.commercial_surcharge != null ? o.commercial_surcharge : s.commercial_surcharge;
    v = Number(v);
    return isFinite(v) ? v : 3;
  }

  function money(n) {
    return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2));
  }

  function radio(scope, value) {
    return (scope || document).querySelector('input[name="address_type"][value="' + value + '"]');
  }

  // ---- warning popup -----------------------------------------------------

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css = [
      '.dcp{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(0,0,0,.45);animation:dcp-fade .16s ease-out}',
      '.dcp__card{box-sizing:border-box;width:100%;max-width:440px;max-height:calc(100vh - 32px);overflow:auto;background:#fff;color:#1a1a1a;border-radius:16px;padding:22px 22px 18px;box-shadow:0 12px 40px rgba(0,0,0,.25);animation:dcp-rise .18s ease-out}',
      '.dcp__icon{display:inline-flex;align-items:center;justify-content:center;width:38px;height:38px;margin-bottom:12px;border-radius:50%;background:rgba(253,193,105,.28);color:#9a6410}',
      '.dcp__icon svg{width:20px;height:20px}',
      '.dcp__title{margin:0 0 8px;font-size:1.1rem;font-weight:700;line-height:1.3;letter-spacing:-.01em}',
      '.dcp__body{margin:0 0 18px;font-size:.95rem;line-height:1.5;color:rgba(0,0,0,.72)}',
      '.dcp__actions{display:flex;flex-direction:column;gap:8px}',
      '.dcp__btn{box-sizing:border-box;width:100%;min-height:46px;padding:11px 16px;border-radius:999px;font:inherit;font-weight:600;cursor:pointer;border:1px solid #1a1a1a;transition:background .15s ease,border-color .15s ease}',
      '.dcp__btn--primary{background:#1a1a1a;color:#fff}',
      '.dcp__btn--primary:hover{background:#000}',
      '.dcp__btn--ghost{background:#fff;color:#1a1a1a;border-color:rgba(0,0,0,.2)}',
      '.dcp__btn--ghost:hover{border-color:rgba(0,0,0,.45)}',
      '.dcp__btn:focus-visible{outline:none;box-shadow:0 0 0 3px rgba(253,193,105,.9)}',
      '@keyframes dcp-fade{from{opacity:0}to{opacity:1}}',
      '@keyframes dcp-rise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}',
      '@media (prefers-reduced-motion:reduce){.dcp,.dcp__card{animation:none}}'
    ].join('');
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = css;
    document.head.appendChild(style);
  }

  var lastFocus = null;
  var prevOverflow = '';

  function close() {
    var root = document.getElementById(ROOT_ID);
    if (!root) return;
    root.parentNode.removeChild(root);
    document.removeEventListener('keydown', onKey, true);
    document.body.style.overflow = prevOverflow;
    if (lastFocus && document.contains(lastFocus) && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  // Puts the customer back on commercial and lets the widget recalculate.
  function revertToCommercial() {
    var commercial = radio(document, 'commercial');
    close();
    if (!commercial) return;
    commercial.checked = true;
    commercial.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Escape and a tap outside the card count as "keep commercial", the safe
  // side of the choice, so the warning can't be dismissed into a wrong venue.
  function onKey(e) {
    var root = document.getElementById(ROOT_ID);
    if (!root) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      revertToCommercial();
      return;
    }
    if (e.key !== 'Tab') return;
    var btns = root.querySelectorAll('button');
    if (!btns.length) return;
    var first = btns[0];
    var last = btns[btns.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function open() {
    if (document.getElementById(ROOT_ID)) return;
    injectStyle();
    lastFocus = document.activeElement;
    prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    var amount = money(surcharge());
    var root = document.createElement('div');
    root.id = ROOT_ID;
    root.className = 'dcp';
    root.innerHTML =
      '<div class="dcp__card" role="alertdialog" aria-modal="true" aria-labelledby="dcp-title" aria-describedby="dcp-body">' +
        '<span class="dcp__icon" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/>' +
          '</svg>' +
        '</span>' +
        '<h2 class="dcp__title" id="dcp-title"></h2>' +
        '<p class="dcp__body" id="dcp-body"></p>' +
        '<div class="dcp__actions">' +
          '<button type="button" class="dcp__btn dcp__btn--primary" data-dcp="commercial"></button>' +
          '<button type="button" class="dcp__btn dcp__btn--ghost" data-dcp="residential">My address is residential</button>' +
        '</div>' +
      '</div>';

    root.querySelector('#dcp-title').textContent = 'Commercial Building Delivery (+' + amount + ')';
    root.querySelector('#dcp-body').textContent = BODY.replace('$3', function () { return amount; });
    root.querySelector('[data-dcp="commercial"]').textContent = 'Keep commercial (+' + amount + ')';

    root.addEventListener('click', function (e) {
      var t = e.target;
      if (t === root) { revertToCommercial(); return; }
      var btn = t.closest && t.closest('[data-dcp]');
      if (!btn) return;
      if (btn.getAttribute('data-dcp') === 'commercial') revertToCommercial();
      else close();
    });

    document.body.appendChild(root);
    document.addEventListener('keydown', onKey, true);
    root.querySelector('[data-dcp="commercial"]').focus();
  }

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || t.name !== 'address_type' || t.value !== 'residential' || !t.checked) return;
    if (e.isTrusted === false) return;
    open();
  });
})();
